import React from 'react';
import { motion } from 'framer-motion';
import ReactECharts from 'echarts-for-react';
import { budgetData } from '../data/mockData';
import SafeIcon from '../components/SafeIcon';
import { FiTrendingUp, FiAlertCircle } from 'react-icons/fi';

export default function BudgetView() {
  const chartOptions = {
    tooltip: { trigger: 'item', backgroundColor: 'rgba(10, 17, 40, 0.9)', borderColor: '#00F0FF', textStyle: { color: '#fff' } },
    legend: { show: false },
    series: [
      {
        name: 'Budget',
        type: 'pie',
        radius: ['60%', '80%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 10,
          borderColor: '#050A15',
          borderWidth: 4
        },
        label: { show: false, position: 'center' },
        emphasis: {
          label: { show: true, fontSize: 20, fontWeight: 'bold', color: '#fff' }
        },
        labelLine: { show: false },
        data: budgetData.categories.map(c => ({ value: c.value, name: c.name, itemStyle: { color: c.color } }))
      }
    ]
  };

  const percentage = Math.round((budgetData.spent / budgetData.total) * 100);
  const isWarning = percentage > 80;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
      <div className="mb-8">
        <h2 className="text-2xl font-mono text-white mb-2">FINANCIAL NOTES</h2>
        <p className="text-gray-400 text-sm">Track your expenditures across dimensions.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Overview */}
        <div className="glass-card p-6 lg:col-span-2 flex flex-col md:flex-row items-center gap-8">
          <div className="w-full md:w-1/2 h-[250px]">
            <ReactECharts option={chartOptions} style={{ height: '100%', width: '100%' }} />
          </div>
          <div className="w-full md:w-1/2 space-y-6">
            <div>
              <p className="text-gray-400 font-mono text-sm mb-1">TOTAL BUDGET</p>
              <h3 className="text-3xl font-light text-white">${budgetData.total.toLocaleString()}</h3>
            </div>
            <div>
              <div className="flex justify-between items-end mb-2">
                <p className="text-gray-400 font-mono text-sm">SPENT SO FAR</p>
                <span className={`font-mono ${isWarning ? 'text-coral-400' : 'text-cyan-400'}`}>{percentage}%</span>
              </div>
              <h3 className={`text-4xl font-bold ${isWarning ? 'neon-text-coral' : 'neon-text-cyan'}`}>
                ${budgetData.spent.toLocaleString()}
              </h3>
            </div>
            
            {isWarning && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-coral-400/10 border border-coral-400/30 text-coral-400 text-sm">
                <SafeIcon icon={FiAlertCircle} className="mt-0.5 flex-shrink-0" />
                <p>Approaching budget limit. Consider optimizing upcoming expenses.</p>
              </div>
            )}
          </div>
        </div>

        {/* Categories List */}
        <div className="glass-card p-6">
          <h3 className="font-mono text-sm text-gray-400 mb-6 border-b border-white/10 pb-2">BREAKDOWN</h3>
          <div className="space-y-5">
            {budgetData.categories.map((cat, idx) => (
              <div key={idx}>
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color }}></span>
                    <span className="text-sm text-gray-200">{cat.name}</span>
                  </div>
                  <span className="font-mono text-sm text-white">${cat.value}</span>
                </div>
                <div className="w-full h-1.5 bg-black/40 rounded-full overflow-hidden">
                  <div 
                    className="h-full rounded-full" 
                    style={{ width: `${(cat.value / budgetData.spent) * 100}%`, backgroundColor: cat.color }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
          
          <button className="w-full mt-8 py-3 rounded-xl border border-dashed border-white/20 text-gray-400 hover:text-white hover:border-white/50 transition-all font-mono text-sm flex items-center justify-center gap-2">
            <SafeIcon icon={FiTrendingUp} />
            ADD EXPENSE
          </button>
        </div>
      </div>
    </motion.div>
  );
}